/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 *
 * Hook encapsulating drag-selection logic for the slot grid.
 */

import { useMemo, useRef, useState } from 'react'
import type { SlotMeta } from './types'

const DRAG_HIGHLIGHT = 'cell-drag-highlight'

function getCellInfo(target: HTMLElement): { hour: number; courtId: string } | null {
  const cell = target.closest('[data-field]') as HTMLElement | null
  const row = target.closest('[data-id]') as HTMLElement | null
  if (!cell || !row) return null
  const field = cell.dataset.field
  const rowId = row.dataset.id
  if (!field || field === 'hour' || rowId == null) return null
  return { hour: Number(rowId), courtId: field }
}

function getCellRect(
  start: { hour: number; courtId: string },
  end: { hour: number; courtId: string },
  courtIds: string[],
) {
  const minHour = Math.min(start.hour, end.hour)
  const maxHour = Math.max(start.hour, end.hour)
  const si = courtIds.indexOf(start.courtId)
  const ei = courtIds.indexOf(end.courtId)
  if (si === -1 || ei === -1) return []
  const minCol = Math.min(si, ei)
  const maxCol = Math.max(si, ei)
  const cells: { hour: number; courtId: string }[] = []
  for (let h = minHour; h <= maxHour; h++) {
    for (let c = minCol; c <= maxCol; c++) {
      cells.push({ hour: h, courtId: courtIds[c] })
    }
  }
  return cells
}

interface UseDragSelectionParams {
  courtIds: string[]
  pastHourThreshold: number
  slotLookup: Map<string, SlotMeta>
  spanKeyMap: Map<string, string>
  spanGroupCells: Map<string, { hour: number; courtId: string }[]>
}

export function useDragSelection({
  courtIds,
  pastHourThreshold,
  slotLookup,
  spanKeyMap,
  spanGroupCells,
}: UseDragSelectionParams) {
  const [selectedCells, setSelectedCells] = useState<
    { hour: number; courtId: string }[]
  >([])
  const gridRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const dragStart = useRef<{ hour: number; courtId: string } | null>(null)
  const isCtrlDrag = useRef(false)
  const pastRef = useRef(pastHourThreshold)
  pastRef.current = pastHourThreshold

  const selectedKeys = useMemo(
    () => new Set(selectedCells.map((c) => `${c.hour}-${c.courtId}`)),
    [selectedCells],
  )

  const clearHighlight = () => {
    gridRef.current?.querySelectorAll(`.${DRAG_HIGHLIGHT}`).forEach((el) => {
      el.classList.remove(DRAG_HIGHLIGHT)
    })
  }

  const highlightRect = (
    start: { hour: number; courtId: string },
    end: { hour: number; courtId: string },
  ) => {
    clearHighlight()
    for (const { hour, courtId } of getCellRect(start, end, courtIds)) {
      if (hour < pastRef.current) continue
      const row = gridRef.current?.querySelector(`[data-id="${hour}"]`)
      row?.querySelector(`[data-field="${courtId}"]`)?.classList.add(DRAG_HIGHLIGHT)
    }
  }

  const expandSpanGroups = (cells: { hour: number; courtId: string }[]) => {
    const result = new Map<string, { hour: number; courtId: string }>()
    for (const cell of cells) {
      const spanKey = spanKeyMap.get(`${cell.hour}-${cell.courtId}`)
      const group = spanKey ? (spanGroupCells.get(spanKey) ?? [cell]) : [cell]
      for (const c of group) {
        result.set(`${c.hour}-${c.courtId}`, c)
      }
    }
    return [...result.values()]
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const info = getCellInfo(e.target as HTMLElement)
    if (!info || info.hour < pastRef.current) {
      if (!e.ctrlKey) setSelectedCells([])
      return
    }
    isDragging.current = true
    dragStart.current = info
    isCtrlDrag.current = e.ctrlKey
    clearHighlight()
    highlightRect(info, info)
    e.preventDefault()
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !dragStart.current) return
    const info = getCellInfo(e.target as HTMLElement)
    if (!info) return
    highlightRect(dragStart.current, info)
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging.current || !dragStart.current) return
    isDragging.current = false
    const end = getCellInfo(e.target as HTMLElement) || dragStart.current
    const cells = expandSpanGroups(
      getCellRect(dragStart.current, end, courtIds).filter(
        (c) => c.hour >= pastRef.current && slotLookup.has(`${c.hour}-${c.courtId}`),
      ),
    )
    dragStart.current = null
    clearHighlight()
    if (isCtrlDrag.current) {
      const newKeys = new Set(cells.map((c) => `${c.hour}-${c.courtId}`))
      setSelectedCells((prev) => {
        const removed = prev.filter((c) => !newKeys.has(`${c.hour}-${c.courtId}`))
        const added = cells.filter((c) => !selectedKeys.has(`${c.hour}-${c.courtId}`))
        return [...removed, ...added]
      })
    } else {
      setSelectedCells(cells)
    }
  }

  return {
    selectedCells,
    setSelectedCells,
    selectedKeys,
    gridRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  }
}

export { DRAG_HIGHLIGHT }
